import axios from 'axios';
import { PEERTUBE_INSTANCES } from '../contexts/PeerTubeContext';

// Create a configurable API instance
const createApi = (baseURL: string) => {
  return axios.create({
    baseURL,
  });
};

// Default API instance
const api = createApi(PEERTUBE_INSTANCES[0].apiUrl);

export interface Video {
  id: number;
  uuid: string;
  name: string;
  description: string;
  thumbnailPath: string;
  previewPath: string;
  embedPath: string;
  views: number;
  likes: number;
  dislikes: number;
  duration: number;
  createdAt: string;
  publishedAt: string;
  tags?: string[];
  channel: {
    name: string;
    displayName: string;
    description: string;
    avatarPath: string;
  };
  account: {
    name: string;
    displayName: string;
    description: string;
    avatarPath: string;
  };
}

export interface VideoListResponse {
  data: Video[];
  total: number;
}

export const fetchVideos = async (apiUrl: string, start = 0, count = 30, sort = '-publishedAt'): Promise<VideoListResponse> => {
  const customApi = createApi(apiUrl);
  const response = await customApi.get('/videos', {
    params: {
      start,
      count,
      sort,
      nsfw: false,
    },
  });
  
  // Ensure the response has the expected structure
  if (!response.data || !Array.isArray(response.data.data)) {
    throw new Error('Invalid API response format');
  }
  
  return response.data;
};

export const fetchVideo = async (apiUrl: string, id: string): Promise<Video> => {
  const customApi = createApi(apiUrl);
  const response = await customApi.get(`/videos/${id}`);
  return response.data;
};

export const getFullThumbnailUrl = (path: string, instanceUrl: string): string => {
  if (!path) return ''; // Handle undefined or empty paths
  if (path.startsWith('http')) return path;
  
  // Some PeerTube instances might have different thumbnail path structures
  // For tube.virtuelle-ph.at, we need to adjust the path format
  if (instanceUrl.includes('virtuelle-ph.at')) {
    // If the path is in the format /lazy-static/thumbnails/..., convert to the proper format
    if (path.includes('/lazy-static/thumbnails/')) {
      const uuid = path.split('/').pop()?.split('.')[0];
      if (uuid) {
        return `${instanceUrl}/static/thumbnails/${uuid}.jpg`;
      }
    }
  }
  
  return `${instanceUrl}${path}`;
};

export const getVideoUrl = (uuid: string, instanceUrl: string): string => {
  return `${instanceUrl}/videos/watch/${uuid}`;
};

export const getVideoEmbedUrl = (instanceUrl: string, uuid: string): string => {
  return `${instanceUrl}/videos/embed/${uuid}`;
};

export default api;
